import React, { useState, useEffect } from "react";
import { X,  Search } from "lucide-react";
import Icons from "../../../components/icons";
import { authFetch } from "../../../lib/api";

const CATEGORIES = [
  "Laptop",
  "Mobile",
  "Headset",
  "Broadband",
  "Other",
];

const AssignAssetModal = ({ onClose, onSuccess }) => {
  const [users, setUsers] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [showUserDrop, setShowUserDrop] = useState(false);
  const [form, setForm] = useState({
    userId: "",
    userName: "",
    employeeId: "",
    category: "",
    inventoryItemId: "",
    assignDate: "",
    returnDate: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [loadingEmployeeId, setLoadingEmployeeId] = useState(false);

  // Fetch active users
  useEffect(() => {
    const load = async () => {
      try {
        const res = await authFetch("/api/admin/users?picker=true");
        const json = await res.json();
        if (json?.data) setUsers(json.data);
      } catch {}
    };
    load();
  }, []);

  // The picker list is a lightweight response (no employeeId) — fetch the
  // full profile for the chosen user so their real Employee ID shows up.
  const selectUser = async (u) => {
    setForm((f) => ({
      ...f,
      userId: u._id,
      userName: u.name,
      employeeId: "",
    }));
    setShowUserDrop(false);
    setUserSearch("");

    setLoadingEmployeeId(true);
    try {
      const res = await authFetch(`/api/admin/users/${u._id}`);
      const json = await res.json();
      setForm((f) =>
        f.userId === u._id
          ? { ...f, employeeId: json?.data?.employeeId || "" }
          : f,
      );
    } catch {
    } finally {
      setLoadingEmployeeId(false);
    }
  };

  // Fetch available inventory whenever category changes
  useEffect(() => {
    if (!form.category) {
      setInventory([]);
      return;
    }
    const load = async () => {
      try {
        const res = await authFetch(
          `/api/assets/inventory/available?category=${encodeURIComponent(form.category)}`,
        );
        const json = await res.json();
        if (json?.data) setInventory(json.data);
      } catch {}
    };
    load();
  }, [form.category]);

  const filteredUsers = users.filter(
    (u) =>
      u.name.toLowerCase().includes(userSearch.toLowerCase()) ||
      (u.email || "").toLowerCase().includes(userSearch.toLowerCase()),
  );

  const handleSubmit = async () => {
    setError("");
    if (!form.userId) {
      setError("Please select a resource.");
      return;
    }
    if (!form.inventoryItemId) {
      setError("Please select an asset.");
      return;
    }
    if (!form.assignDate) {
      setError("Please enter the assigned date.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await authFetch("/api/assets/assign", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId: form.userId,
          inventoryItemId: form.inventoryItemId,
          assignDate: form.assignDate,
          returnDate: form.returnDate || undefined,
        }),
      });
      const json = await res.json();
      if (res.ok && json?.data) {
        onSuccess(json.data);
        onClose();
      } else setError(json?.message || "Failed to assign asset.");
    } catch {
      setError("Network error.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 z-[2000] flex items-center justify-center p-4 font-['Poppins']">
      <div className="w-full max-w-[720px] bg-white rounded-xl shadow-2xl p-10">
        {/* Header */}
        <div className="flex items-start justify-between mb-1">
          <div>
            <h2 className="text-2xl font-semibold text-black">
              Assign Asset
            </h2>
            <p className="text-sm text-black/50 mt-1">
              Fill in the details to assign an asset to a resource
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-black/50 hover:text-black transition"
          >
            <X size={22} />
          </button>
        </div>

        <hr className="my-6 border-black/[0.07]" />

        {error && <p className="text-xs text-red-500 mb-4">{error}</p>}

        {/* Name + Employee ID row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="text-base text-black mb-2 block">Name</label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowUserDrop((v) => !v)}
                className="w-full h-10 border border-black/[0.19] rounded-lg px-3 text-sm text-left flex items-center justify-between bg-white capitalize"
              >
                <span
                  className={form.userName ? "text-black" : "text-black/30"}
                >
                  {form.userName || "Select"}
                </span>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#000"
                  strokeWidth="2"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              {showUserDrop && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowUserDrop(false)}
                  />
                  <div className="absolute top-full mt-1 left-0 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-20 overflow-hidden">
                    <div className="flex items-center gap-2 border-b px-3 py-2">
                      <Search size={13} className="text-gray-400" />
                      <input
                        autoFocus
                        type="text"
                        placeholder="Search Members"
                        className="text-xs outline-none flex-1 bg-transparent"
                        value={userSearch}
                        onChange={(e) => setUserSearch(e.target.value)}
                      />
                    </div>
                    <div className="max-h-40 overflow-y-auto">
                      {filteredUsers.length === 0 ? (
                        <p className="text-xs text-gray-400 text-center py-3">
                          No users found
                        </p>
                      ) : (
                        filteredUsers.map((u) => (
                          <button
                            key={u._id}
                            type="button"
                            onClick={() => selectUser(u)}
                            className="flex items-center gap-3 w-full text-left px-3 py-2 hover:bg-gray-50"
                          >
                            <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-bold text-blue-600 flex-shrink-0">
                              {u.name[0].toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <p className="text-xs font-medium text-gray-800 truncate">
                                {u.name} - {u.role}
                              </p>
                              <p className="text-[10px] text-gray-400 truncate">
                                {u.email}
                              </p>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          <div>
            <label className="text-base text-black mb-2 block">
              Employee ID
            </label>
            <input
              type="text"
              readOnly
              value={loadingEmployeeId ? "Loading..." : form.employeeId || ""}
              placeholder="Auto-filled"
              onChange={() => {}}
              className="w-full h-10 border-0 rounded-lg px-3 text-sm bg-[#F1F5F9] text-black outline-none"
            />
          </div>
        </div>

        {/* Asset Type */}
        <div className="mb-6">
          <label className="text-base text-black mb-2 block">
            Asset Type
          </label>
          <select
            value={form.category}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                category: e.target.value,
                inventoryItemId: "",
              }))
            }
            className="w-full h-10 border border-black/[0.19] rounded-lg px-3 text-sm outline-none bg-white"
          >
            <option value="">Select Asset Type</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        {/* Select Asset */}
        <div className="mb-6">
          <label className="text-base text-black mb-2 block">
            Select Asset
          </label>
          <select
            value={form.inventoryItemId}
            onChange={(e) =>
              setForm((f) => ({ ...f, inventoryItemId: e.target.value }))
            }
            disabled={!form.category}
            className="w-full h-10 border border-black/[0.19] rounded-lg px-3 text-sm outline-none bg-white disabled:opacity-50 capitalize"
          >
            <option value="">
              {form.category
                ? inventory.length === 0
                  ? "No available assets"
                  : "Select Asset"
                : "Select Asset Type first"}
            </option>
            {inventory.map((item) => {
              const ownerName =
                item.resource?.name ||
                item.assignedTo?.name ||
                item.assignedUser?.name ||
                item.employee?.name ||
                "";
              const label =
                form.category === "ID Card"
                  ? ownerName
                    ? `ID Card — ${ownerName}`
                    : `ID Card${item.serialNumber ? ` — ${item.serialNumber}` : ""}`
                  : `${item.modelName || item.name || item.category} ${
                      item.serialNumber ? `— ${item.serialNumber}` : ""
                    }`;
              return (
                <option key={item._id} value={item._id}>
                  {label}
                </option>
              );
            })}
          </select>
        </div>

        {/* Dates */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-10">
          <div>
            <label className="text-base text-black mb-2 block">
              Assigned Date
            </label>
            <div className="relative flex items-center border border-black/[0.19] rounded-lg overflow-hidden h-10">
              <div className="flex items-center justify-center w-[67px] h-10 bg-black/[0.04] flex-shrink-0">
                <Icons.Calendar/>
              </div>
              <input
                type="date"
                value={form.assignDate}
                onChange={(e) =>
                  setForm((f) => ({ ...f, assignDate: e.target.value }))
                }
                className="w-full h-full px-3 text-sm outline-none border-0"
              />
            </div>
          </div>
          <div>
            <label className="text-base text-black mb-2 block">
              Return Date
            </label>
            <div className="relative flex items-center border border-black/[0.19] rounded-lg overflow-hidden h-10">
              <div className="flex items-center justify-center w-[67px] h-10 bg-black/[0.04] flex-shrink-0">
                      <Icons.Calendar />
              </div>
              <input
                type="date"
                value={form.returnDate}
                onChange={(e) =>
                  setForm((f) => ({ ...f, returnDate: e.target.value }))
                }
                min={form.assignDate}
                className="w-full h-full px-3 text-sm outline-none border-0"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="w-[163px] h-10 border border-[#4253C7] rounded-lg text-[#4253C7] text-sm hover:bg-indigo-50 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="w-[163px] h-10 bg-[#4253C7] text-white rounded-lg text-sm hover:bg-[#3644b0] transition disabled:opacity-60"
          >
            {submitting ? "Assigning..." : "Assign"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignAssetModal;